import { z } from 'zod';
import { apiClient } from '@api/client';
import type { ApiResponseShape, CommonResponse } from '@/types/api';
import {
  cropDetailSchema,
  cropOptionSchema,
  cropSchema,
  type Crop,
  type CropDetail,
  type CropFormValues,
  type CropOption,
  type ListCropsParams,
} from './schema';

const BASE_PATH = '/crops';

const listResponseSchema = z.object({
  message: z.string(),
  columnDefs: z.array(z.record(z.unknown())).optional(),
  rowData: z.array(cropSchema),
  gridOptions: z.record(z.unknown()).optional(),
  paginationDetails: z
    .object({
      page: z.number(),
      pageSize: z.number(),
      totalRecords: z.number(),
      isLastPage: z.boolean(),
    })
    .optional(),
});

const detailResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: cropDetailSchema.nullable(),
  error: z.string().nullable().optional(),
});

const recordResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: cropSchema.nullable(),
  error: z.string().nullable().optional(),
});

const optionsResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: z.array(cropOptionSchema).nullable(),
  error: z.string().nullable().optional(),
});

function pruneParams(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

function toWireTranslations(values: CropFormValues) {
  return values.translations
    .filter((t) => t.name.trim().length > 0)
    .map((t) => ({ localeCode: t.localeCode, name: t.name.trim() }));
}

function toCreatePayload(values: CropFormValues) {
  return {
    code: values.code,
    name: values.name,
    category: values.category ?? undefined,
    imageStorageKey: values.imageStorageKey ?? undefined,
    thumbnailStorageKey: values.thumbnailStorageKey ?? undefined,
    displayOrder: values.displayOrder,
    isPreselected: values.isPreselected,
    isActive: values.isActive,
    translations: toWireTranslations(values),
  };
}

// `code` intentionally absent — immutable post-create.
function toUpdatePayload(values: CropFormValues) {
  return {
    name: values.name,
    category: values.category,
    imageStorageKey: values.imageStorageKey,
    thumbnailStorageKey: values.thumbnailStorageKey,
    displayOrder: values.displayOrder,
    isPreselected: values.isPreselected,
    isActive: values.isActive,
    translations: toWireTranslations(values),
  };
}

export async function listCrops(
  params: ListCropsParams,
): Promise<CommonResponse<Crop>> {
  const response = await apiClient.get(BASE_PATH, {
    params: pruneParams(params as unknown as Record<string, unknown>),
  });
  const parsed = listResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`Invalid list response: ${parsed.error.message}`);
  }
  return parsed.data;
}

export async function getCrop(id: string): Promise<CropDetail> {
  const response = await apiClient.get(`${BASE_PATH}/${id}`);
  const parsed = detailResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function getCropOptions(): Promise<CropOption[]> {
  const response = await apiClient.get(`${BASE_PATH}/options`);
  const parsed = optionsResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function createCrop(values: CropFormValues): Promise<Crop> {
  const response = await apiClient.post<ApiResponseShape<Crop>>(
    BASE_PATH,
    toCreatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function updateCrop(
  id: string,
  values: CropFormValues,
): Promise<Crop> {
  const response = await apiClient.put<ApiResponseShape<Crop>>(
    `${BASE_PATH}/${id}`,
    toUpdatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}
